// ============================================================================
// COURT PLACE MASTER SCREEN — MSTR-COURTPL-001
// ============================================================================
// Uses bms.tbl_state_province FK for State. Loads India states from the
// read-only /StateProvince endpoint and passes them to the form modal.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ColumnSelector, type ColumnDefinition } from 'components/common';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  ActionMenu,
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  MinimizedModalStrip,
} from 'components/ui';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useCourtPlaces } from 'hooks/api/useCourtPlaces';
import { useStateProvinces } from 'hooks/api/useStateProvinces';
import { courtPlaceService } from 'api/services/courtPlaceService';
import type { CourtPlace, CourtPlaceSearchRequest } from 'api/types/courtPlace.types';
import CourtPlaceFormModal, { type StateProvinceOption } from 'components/modals/modules/court-place-module/CourtPlaceFormModal';

type ViewMode = 'list' | 'grid';

interface CourtPlaceMasterScreenProps {
  tabId?: string;
  moduleName?: string;
}

const CourtPlaceMasterScreen: React.FC<CourtPlaceMasterScreenProps> = ({ tabId, moduleName }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();

  const [queryParams, setQueryParams] = useState<CourtPlaceSearchRequest>({ page: 1, pageSize: 25 });
  const { data, loading, pagination, refetch } = useCourtPlaces(queryParams);

  // India states (active only) for form dropdown.
  const { data: states, refetch: refetchStates } = useStateProvinces({ countryCode: 'IN', pageSize: 100 });
  // All states (incl. inactive/deleted) for filter + seed label lookup.
  const { data: allStates, refetch: refetchAllStates } = useStateProvinces({ countryCode: 'IN', pageSize: 100, statusCode: 'ALL' });

  const stateProvinceOptions: StateProvinceOption[] = useMemo(
    () => (states ?? []).map(s => ({
      value: s.id,
      label: `${s.provinceCode} — ${s.provinceName}`,
    })),
    [states]
  );

  const stateFilterOptions = useMemo(() => {
    const list = (allStates ?? []).map(s => ({
      value: s.id,
      label: `${s.provinceCode} — ${s.provinceName}`,
    }));
    return [{ value: '', label: 'All states' }, ...list];
  }, [allStates]);

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  const [placeCodeFilter, setPlaceCodeFilter] = useState('');
  const [placeNameFilter, setPlaceNameFilter] = useState('');
  const [stateFilter, setStateFilter] = useState<string>('');
  const [pincodeFilter, setPincodeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  useEffect(() => {
    const t = setTimeout(() => {
      setQueryParams(p => ({ ...p, searchTerm: searchQuery.trim() || undefined, page: 1 }));
    }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'copy' | 'view'>('create');
  const [selected, setSelected] = useState<CourtPlace | null>(null);

  // Seed option for inactive/deleted state — shows its label even when not in active list.
  const seedStateProvinceOptions: StateProvinceOption[] = useMemo(() => {
    if (formMode !== 'edit' || !selected || !selected.stateProvinceId) return [];
    const spid = selected.stateProvinceId;
    if (stateProvinceOptions.some(o => o.value === spid)) return [];
    const fromAll = (allStates ?? []).find(s => s.id === spid);
    const label = fromAll
      ? `${fromAll.provinceCode} — ${fromAll.provinceName} (Inactive)`
      : `${selected.stateProvinceCode ?? '?'} — ${selected.stateProvinceName ?? 'Unknown'} (Inactive)`;
    return [{ value: spid, label }];
  }, [formMode, selected, stateProvinceOptions, allStates]);

  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState<string>('0');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  const handleRestoreModal = useCallback(
    (restoredMode: 'create' | 'edit' | 'copy' | 'view', instanceId?: string, recordCode?: string) => {
      refetchStates();
      refetchAllStates();
      if (instanceId) setCreateInstanceId(instanceId);
      if (recordCode) {
        const rec = data.find(p => p.placeCode === recordCode);
        if (rec) setSelected(rec);
      }
      setFormMode(restoredMode);
      setFormModalOpen(true);
    },
    [data, refetchStates, refetchAllStates]
  );

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toDelete, setToDelete] = useState<CourtPlace | null>(null);

  const handleAddNew = useCallback(() => {
    refetchStates();
    refetchAllStates();
    if (formModalOpen) formModalMinimizeRef.current?.();
    const newId = String(++createInstanceCounter.current);
    setCreateInstanceId(newId);
    setSelected(null);
    setFormMode('create');
    setFormModalOpen(true);
  }, [formModalOpen, refetchStates, refetchAllStates]);

  const handleEdit = useCallback(
    (p: CourtPlace) => {
      refetchStates();
      refetchAllStates();
      const modalId = tabId ? `${tabId}-form-modal-edit-${p.placeCode}` : null;
      if (modalId && isMinimized(modalId)) {
        const modal = getMinimizedModal(modalId);
        dismissMinimized(modalId);
        modal?.onRestore?.();
        return;
      }
      setSelected(p);
      setFormMode('edit');
      setFormModalOpen(true);
    },
    [tabId, isMinimized, dismissMinimized, getMinimizedModal, refetchStates, refetchAllStates]
  );

  const handleView = useCallback(
    (p: CourtPlace) => {
      const modalId = tabId ? `${tabId}-view-modal-${p.placeCode}` : null;
      if (modalId && isMinimized(modalId)) {
        const modal = getMinimizedModal(modalId);
        dismissMinimized(modalId);
        modal?.onRestore?.();
        return;
      }
      setSelected(p);
      setFormMode('view');
      setFormModalOpen(true);
    },
    [tabId, isMinimized, dismissMinimized, getMinimizedModal],
  );

  const handleCopy = useCallback(
    (p: CourtPlace) => {
      setSelected(p);
      setFormMode('copy');
      setFormModalOpen(true);
    },
    [],
  );

  const handleDeleteClick = useCallback((p: CourtPlace) => { setToDelete(p); setDeleteModalOpen(true); }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!toDelete) return;
    setIsDeleting(true);
    try {
      await courtPlaceService.delete(toDelete.id);
      setIsDeleting(false);
      setDeleteModalOpen(false);
      notificationService.toast.success(`Court Place "${toDelete.placeCode}" deleted successfully.`);
      setToDelete(null);
      refetch();
    } catch (err: any) {
      setIsDeleting(false);
      const msg = err?.response?.data?.message || 'An error occurred while deleting.';
      notificationService.toast.error(msg, 6000);
    }
  }, [toDelete, refetch]);

  const handleSaved = useCallback(() => { refetch(); refetchStates(); refetchAllStates(); }, [refetch, refetchStates, refetchAllStates]);
  const handleRefresh = useCallback(() => { refetch(); refetchStates(); refetchAllStates(); }, [refetch, refetchStates, refetchAllStates]);

  const handlePageChange = useCallback((i: number) => setQueryParams(p => ({ ...p, page: i + 1 })), []);
  const handlePageSizeChange = useCallback((s: number) => setQueryParams(p => ({ ...p, pageSize: s, page: 1 })), []);

  const handleApplyFilters = useCallback(() => {
    setQueryParams(p => ({
      ...p,
      placeCode: placeCodeFilter.trim() || undefined,
      placeName: placeNameFilter.trim() || undefined,
      stateProvinceId: stateFilter || undefined,
      pincode: pincodeFilter.trim() || undefined,
      statusCode: statusFilter || undefined,
      page: 1,
    }));
    setFilterOpen(false);
  }, [placeCodeFilter, placeNameFilter, stateFilter, pincodeFilter, statusFilter]);

  const handleClearFilters = useCallback(() => {
    setPlaceCodeFilter(''); setPlaceNameFilter(''); setStateFilter(''); setPincodeFilter(''); setStatusFilter('');
    setQueryParams({ page: 1, pageSize: queryParams.pageSize ?? 25 });
  }, [queryParams.pageSize]);

  const activeFilterCount = useMemo(() => {
    let c = 0;
    if (queryParams.placeCode) c++;
    if (queryParams.placeName) c++;
    if (queryParams.stateProvinceId) c++;
    if (queryParams.pincode) c++;
    if (queryParams.statusCode) c++;
    return c;
  }, [queryParams.placeCode, queryParams.placeName, queryParams.stateProvinceId, queryParams.pincode, queryParams.statusCode]);

  const columns: SyutGridColumn<CourtPlace>[] = useMemo(() => [
    { id: 'placeCode', header: 'Code', accessorKey: 'placeCode', size: 140, enableSorting: true },
    { id: 'placeName', header: 'Name', accessorKey: 'placeName', size: 220, enableSorting: true, flex: true },
    {
      id: 'stateProvinceName', header: 'State', accessorKey: 'stateProvinceName', size: 160, enableSorting: true,
      cell: ({ row }) => row.original.stateProvinceName ?? row.original.stateProvinceCode ?? '-',
    },
    {
      id: 'pincode', header: 'Pincode', accessorKey: 'pincode', size: 110, enableSorting: true,
      cell: ({ row }) => row.original.pincode ?? '-',
    },
    {
      id: 'statusCode', header: 'Status', accessorKey: 'statusCode', size: 110, enableSorting: true,
      cell: ({ row }) => {
        const p = row.original;
        const code = p.deletedAt ? 'DELETED' : (p.statusCode || 'ACTIVE');
        let color = '#6b7280';
        if (code === 'ACTIVE') color = '#16a34a';
        else if (code === 'DELETED') color = '#dc2626';
        return (
          <span style={{
            fontSize: '0.75rem', fontWeight: 600, color,
            padding: '2px 8px', border: `1px solid ${color}`, borderRadius: '999px',
          }} title={p.deletedAt ? `Deleted at ${new Date(p.deletedAt).toLocaleString()}` : undefined}>
            {code}
          </span>
        );
      },
    },
    {
      id: 'updatedAt', header: 'Last Updated', accessorKey: 'updatedAt', size: 150, enableSorting: true,
      cell: ({ row }) => {
        const v = row.original.updatedAt;
        if (!v) return '-';
        try { return new Date(v).toLocaleString(); } catch { return v; }
      },
    },
  ], []);

  // Column visibility (driven by ColumnSelector toolbar control)
  const columnDefinitions: ColumnDefinition[] = useMemo(
    () =>
      columns.map((col) => ({
        id: col.id as string,
        label: typeof col.header === 'string' ? (col.header as string) : (col.id as string),
        defaultVisible: true,
      })),
    [columns],
  );
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() =>
    columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id),
  );
  const handleApplyColumns = useCallback((cols: string[]) => setVisibleColumns(cols), []);
  const handleResetColumns = useCallback(() => {
    setVisibleColumns(columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id));
  }, [columnDefinitions]);

  const getRowActions = useCallback((p: CourtPlace): ActionMenuItem[] => [
    { id: 'view', label: 'View Details', icon: <IconSystem.eye size={16} />, onClick: () => handleView(p) },
    { id: 'edit', label: 'Edit', icon: <IconSystem.edit size={16} />, onClick: () => handleEdit(p) },
    { id: 'copy', label: 'Copy', icon: <IconSystem.copy size={16} />, onClick: () => handleCopy(p) },
    { id: 'delete', label: 'Delete', icon: <IconSystem.trash size={16} />, onClick: () => handleDeleteClick(p), variant: 'danger' },
  ], [handleEdit, handleView, handleCopy, handleDeleteClick]);

  const actionButtons: ActionButton[] = useMemo(() => [
    { id: 'add', icon: <IconSystem.add size={18} />, label: 'Add', tooltip: 'Add New Court Place', onClick: handleAddNew, variant: 'icon' },
    { id: 'refresh', icon: <IconSystem.refresh size={18} />, label: 'Refresh', tooltip: 'Refresh', onClick: handleRefresh, variant: 'icon' },
  ], [handleAddNew, handleRefresh]);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Court Place Master"
        description="Normalized catalog of cities/towns where courts sit. State references bms.tbl_state_province. Referenced by Court Master for location."
        icon={<IconSystem.home size={18} />}
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search code, name, state, pincode...' }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: [
            { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List view' },
            { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Grid view' },
          ],
          onChange: (m) => setViewMode(m as ViewMode),
        }}
        filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen || activeFilterCount > 0, activeFilterCount }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        customToolbarItems={
          <ColumnSelector
            columns={columnDefinitions}
                visibleColumns={visibleColumns}
            onApply={handleApplyColumns}
            onReset={handleResetColumns}
          />
        }
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {viewMode === 'list' && (
              <SyutGrid
                data={data}
                columns={columns}
                enableSorting
                enablePagination
                manualPagination
                totalCount={pagination.totalItems}
                currentPage={(queryParams.page ?? 1) - 1}
                pageSize={queryParams.pageSize ?? 25}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              visibleColumns={visibleColumns}
                rowActions={getRowActions}
                isLoading={loading}
                emptyMessage="No court places found"
              />
            )}

            {viewMode === 'grid' && (
              <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div style={{
                  padding: '1rem', display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                  gap: '1rem', flex: 1, overflow: 'auto', alignContent: 'start',
                }}>
                  {data.map((p) => (
                    <div key={String(p.id)}
                      style={{ position: 'relative', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '1rem', background: 'var(--bg-primary)', cursor: 'pointer' }}
                      onClick={() => handleEdit(p)}>
                      {/* Action menu pinned to the card's top-right; stop click
                          propagation so opening the menu doesn't also fire the
                          card's onClick. */}
                      <div
                        style={{ position: 'absolute', top: 6, right: 6 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ActionMenu items={getRowActions(p)} />
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 600, paddingRight: '1.5rem' }}>
                        {p.placeCode} · {p.stateProvinceName ?? '—'} · {p.pincode ?? '—'}
                      </div>
                      <div style={{ fontSize: '1rem', fontWeight: 600, marginTop: '0.25rem', color: 'var(--text-primary)' }}>{p.placeName}</div>
                      <div style={{ fontSize: '0.75rem', marginTop: '0.5rem', color: p.statusCode === 'ACTIVE' ? '#16a34a' : '#6b7280' }}>
                        {p.statusCode || 'ACTIVE'}
                      </div>
                    </div>
                  ))}
                </div>
                {data.length > 0 && (
                  <div className="pagination-footer">
                    <div className="pagination-info">
                      Showing {data.length} of {pagination.totalItems} items
                    </div>
                    <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <select
                        value={queryParams.pageSize ?? 25}
                        onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                        style={{ padding: '0.375rem 0.5rem', borderRadius: '0.375rem', border: '1px solid var(--border-color)', fontSize: '0.85rem', marginRight: '0.5rem' }}
                      >
                        {[10, 25, 50, 100].map((size) => (
                          <option key={size} value={size}>{size} / page</option>
                        ))}
                      </select>
                      <button className="page-btn" onClick={() => handlePageChange(0)} disabled={(queryParams.page ?? 1) <= 1}>
                        <IconSystem.chevronLeft size={16} />
                        <IconSystem.chevronLeft size={16} style={{ marginLeft: '-8px' }} />
                      </button>
                      <button className="page-btn" onClick={() => handlePageChange((queryParams.page ?? 1) - 2)} disabled={(queryParams.page ?? 1) <= 1}>
                        <IconSystem.chevronLeft size={16} />
                      </button>
                      <span style={{ padding: '0 0.5rem', fontSize: '0.85rem' }}>
                        Page {queryParams.page ?? 1} of {Math.max(1, Math.ceil(pagination.totalItems / (queryParams.pageSize ?? 25)))}
                      </span>
                      <button className="page-btn" onClick={() => handlePageChange(queryParams.page ?? 1)} disabled={(queryParams.page ?? 1) >= Math.max(1, Math.ceil(pagination.totalItems / (queryParams.pageSize ?? 25)))}>
                        <IconSystem.chevronRight size={16} />
                      </button>
                      <button className="page-btn" onClick={() => handlePageChange(Math.max(1, Math.ceil(pagination.totalItems / (queryParams.pageSize ?? 25))) - 1)} disabled={(queryParams.page ?? 1) >= Math.max(1, Math.ceil(pagination.totalItems / (queryParams.pageSize ?? 25)))}>
                        <IconSystem.chevronRight size={16} />
                        <IconSystem.chevronRight size={16} style={{ marginLeft: '-8px' }} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {filterOpen && (
        <div className="search-panel">
          <div className="search-panel-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <IconSystem.filter size={14} />
              <h3 className="search-panel-title">Filters</h3>
            </div>
            <button
              onClick={() => { handleClearFilters(); setFilterOpen(false); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', display: 'flex', alignItems: 'center', color: 'var(--text-muted)', borderRadius: '0.25rem' }}
              title="Close filters"
            >
              <IconSystem.x size={16} />
            </button>
          </div>
          <div className="search-panel-content">
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Place Code</label>
              <input type="text" className="form-input" value={placeCodeFilter}
                onChange={(e) => setPlaceCodeFilter(e.target.value)}
                placeholder="Filter by code..."
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
            </div>
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Place Name</label>
              <input type="text" className="form-input" value={placeNameFilter}
                onChange={(e) => setPlaceNameFilter(e.target.value)}
                placeholder="Filter by name..."
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="State"
                options={stateFilterOptions}
                value={stateFilter}
                onChange={(v) => setStateFilter((v as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Pincode</label>
              <input type="text" className="form-input" value={pincodeFilter}
                onChange={(e) => setPincodeFilter(e.target.value)}
                placeholder="680001"
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Status"
                options={[
                  { value: '', label: 'Active only (default)' },
                  { value: 'INACTIVE', label: 'Inactive only' },
                  { value: 'DELETED', label: 'Deleted only' },
                  { value: 'ALL', label: 'All (incl. deleted)' },
                ]}
                value={statusFilter}
                onChange={(v) => setStatusFilter((v as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={handleClearFilters}>Clear</button>
            <button className="btn btn-primary" onClick={handleApplyFilters}>Apply</button>
          </div>
        </div>
        )}
      </div>

      <ConfirmDialog
        open={deleteModalOpen}
        title="Confirm Delete"
        message={toDelete ? `Are you sure you want to delete court place "${toDelete.placeCode}"? This action cannot be undone.` : ''}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Deleting..."
        onConfirm={handleConfirmDelete}
        onCancel={() => { if (!isDeleting) { setDeleteModalOpen(false); setToDelete(null); } }}
      />

      <CourtPlaceFormModal
        isOpen={formModalOpen}
        mode={formMode}
        record={selected}
        stateProvinceOptions={stateProvinceOptions}
        seedStateProvinceOptions={seedStateProvinceOptions}
        onSaved={handleSaved}
        onClose={() => setFormModalOpen(false)}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreModal={handleRestoreModal}
        createInstanceId={createInstanceId}
        minimizeRef={formModalMinimizeRef}
      />
    </div>
  );
};

export default CourtPlaceMasterScreen;
